import { useState } from 'react';
import { Star, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Trustpilot review-ask modal — Layer 1 in-portal peak-emotion prompt.
 *
 * B2B voice: peer-to-peer to a wedding videographer/photographer. Copy locked
 * as "Option A" in the 2026-07-13 strategy pass.
 *
 * Deflection funnel (Trustpilot-compliant): both branches actually execute.
 *   - 4-5★  → open Trustpilot evaluate URL in new tab (via /click endpoint)
 *   - 1-3★  → private textarea → posts to /private-feedback → Resend email to Ryan
 *
 * Dismissal:
 *   - "Not now"           → 90-day cooldown
 *   - "Don't ask again"   → permanent skip (respects user autonomy)
 */
export default function ReviewAskModal({ project, approvedCount, onClose }) {
  const [stars, setStars] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [showLowStarForm, setShowLowStarForm] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleStar = async (n) => {
    setStars(n);
    if (n >= 4) {
      // High-star path — record + open Trustpilot in new tab.
      setSubmitting(true);
      try {
        const r = await api.post('/review-ask/click', { stars: n });
        window.open(r.data.trustpilot_url, '_blank', 'noopener,noreferrer');
        toast.success('Thank you — that means a lot.');
        onClose();
      } catch { toast.error("Couldn't record — try again in a moment."); }
      setSubmitting(false);
    } else {
      // Low-star path — collapse into private-feedback form.
      setShowLowStarForm(true);
    }
  };

  const handleSendPrivate = async () => {
    if (!feedback.trim() || feedback.length > 4000) return;
    setSubmitting(true);
    try {
      await api.post('/review-ask/private-feedback', { stars, text: feedback.trim() });
      toast.success('Thanks — Ryan will read this personally.');
      onClose();
    } catch { toast.error("Couldn't send — try again in a moment."); }
    setSubmitting(false);
  };

  const handleDismiss = async (permanent) => {
    setSubmitting(true);
    try {
      await api.post('/review-ask/dismiss', { permanent });
      onClose();
    } catch { onClose(); }
    setSubmitting(false);
  };

  return (
    <div
      data-testid="review-ask-modal"
      className="ug-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="tap-auto relative w-full max-w-md rounded-xl bg-ug-surface border border-oxidized-teal/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-6 animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="review-ask-title">
        {/* Close (X) — behaves like "Not now" (90-day cooldown) */}
        <button
          data-testid="review-ask-close"
          onClick={() => handleDismiss(false)}
          disabled={submitting}
          className="tap-auto absolute top-3 right-3 text-slate-500 hover:text-zinc-300 transition-colors"
          aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        {!showLowStarForm ? (
          <>
            {/* Locked copy — Option A, peer-network framing */}
            <h2
              id="review-ask-title"
              data-testid="review-ask-title"
              className="font-serif text-2xl font-semibold text-zinc-100 mb-2 leading-tight">
              Loved <span className="text-ug-gold">"{project?.title || 'that edit'}"?</span>
            </h2>
            <p className="font-sans text-sm text-slate-400 mb-5 leading-relaxed">
              When another wedding filmmaker is hunting for an editing team, a 30-second Trustpilot review from a repeat studio is worth more than anything else we could put on our site.
            </p>

            {/* Star selector — hover states + testids for each star */}
            <div className="flex items-center justify-center gap-2 my-6" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  data-testid={`review-star-${n}`}
                  onClick={() => handleStar(n)}
                  disabled={submitting}
                  onMouseEnter={() => setHoverStar(n)}
                  onMouseLeave={() => setHoverStar(0)}
                  className="tap-auto p-1.5 rounded-lg hover:bg-oxidized-teal/[0.1] transition-all disabled:opacity-50"
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  role="radio"
                  aria-checked={stars === n}>
                  <Star
                    className={`w-9 h-9 transition-all ${
                      (hoverStar || stars) >= n
                        ? 'fill-ug-gold text-ug-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.4)]'
                        : 'text-slate-600'
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {/* Cohort context — soft, not braggy */}
            {approvedCount > 0 && (
              <p className="text-center font-sans text-[10px] text-slate-500 uppercase tracking-wider mb-4">
                {approvedCount} film{approvedCount === 1 ? '' : 's'} approved · you know us by now
              </p>
            )}

            {/* Dismiss row — Not now (90d) + Don't ask again (permanent) */}
            <div className="flex items-center justify-between border-t border-ug-border pt-4 mt-4">
              <button
                data-testid="review-ask-dismiss-cooldown"
                onClick={() => handleDismiss(false)}
                disabled={submitting}
                className="tap-auto font-sans text-xs text-slate-400 hover:text-zinc-100 transition-colors">
                Not now
              </button>
              <button
                data-testid="review-ask-dismiss-permanent"
                onClick={() => handleDismiss(true)}
                disabled={submitting}
                className="tap-auto font-sans text-xs text-slate-500 hover:text-zinc-300 transition-colors">
                Don't ask me again
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Low-star deflection — private feedback to Ryan */}
            <h2
              data-testid="review-ask-lowstar-title"
              className="font-serif text-xl font-semibold text-zinc-100 mb-2 leading-tight">
              Anything we can fix?
            </h2>
            <p className="font-sans text-sm text-slate-400 mb-4 leading-relaxed">
              Goes straight to <span className="text-ug-gold font-semibold">Ryan</span> — not public.
            </p>
            <textarea
              data-testid="review-ask-lowstar-text"
              value={feedback}
              onChange={e => setFeedback(e.target.value.slice(0, 4000))}
              placeholder="What would you change?"
              rows={5}
              autoFocus
              disabled={submitting}
              /* bg-ug-surface, not bg-ug-base: a textarea is a CONTROL. bg-ug-base
                 is the page, which the light ramp collapses into bg-ug-deepest, so
                 this input rendered the same value as the surface behind it. */
              className="tap-auto w-full bg-ug-surface border border-ug-border rounded-lg px-3 py-2.5 text-sm text-zinc-100 placeholder:text-slate-600 focus:outline-none focus:border-oxidized-teal/50 resize-none" />
            <div className="flex items-center justify-between mt-4 gap-3">
              <button
                data-testid="review-ask-lowstar-back"
                onClick={() => { setShowLowStarForm(false); setStars(0); }}
                disabled={submitting}
                className="tap-auto font-sans text-xs text-slate-500 hover:text-zinc-300 transition-colors">
                Back
              </button>
              <button
                data-testid="review-ask-lowstar-send"
                onClick={handleSendPrivate}
                disabled={submitting || !feedback.trim()}
                className="tap-auto px-5 py-2 rounded-lg bg-oxidized-teal text-zinc-950 font-sans text-xs font-semibold uppercase tracking-wider hover:bg-oxidized-teal/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? 'Sending…' : 'Send it'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
